<template>
  <div :class="$style.base">
    <div :class="$style.visualHolder">
      <img :src="company.settings.giftcard_picture || defaultVisualUrl" alt="Giftcard" :class="$style.giftCardVisual" :style="isCustomVisual ? {} : {
        transform: 'rotate(-8.976deg)'
      }"/>
      <svg v-if="!isCustomVisual" width="180" height="135" viewBox="0 0 180 135" fill="none" :class="$style.sparkles">
        <path :class="$style.sparkle_one" d="M173.085 87.9936C173.132 87.7647 173.457 87.7588 173.513 87.9859L174.796 93.2008L179.412 94.0808C179.649 94.1261 179.65 94.4658 179.412 94.5121L174.796 95.4138L173.513 100.606C173.457 100.833 173.133 100.827 173.085 100.599L172.001 95.4138L167.198 94.5124C166.959 94.4675 166.96 94.1244 167.199 94.0805L172.001 93.2008L173.085 87.9936Z" fill="url(#paint0_linear_308_1366)"/>
        <path d="M158.913 130.181C158.913 132.349 128.039 134.106 89.9543 134.106C51.8696 134.106 20.9959 132.349 20.9959 130.181C20.9959 128.014 51.8696 126.257 89.9543 126.257C128.039 126.257 158.913 128.014 158.913 130.181Z" fill="#E5E8F2"/>
        <path :class="$style.sparkle_two" d="M23.04 5.04872C23.114 4.69312 23.6186 4.68404 23.7054 5.03675L25.6986 13.1386L32.8692 14.5058C33.2381 14.5761 33.2393 15.1039 32.8707 15.1758L25.6986 16.5766L23.7053 24.6438C23.6182 24.9961 23.1143 24.987 23.0401 24.6318L21.3559 16.5766L13.895 15.1763C13.5231 15.1065 13.5243 14.5735 13.8964 14.5054L21.3559 13.1386L23.04 5.04872Z" fill="url(#paint18_linear_308_1366)"/>
        <path :class="$style.sparkle_three" d="M6.35749 77.6305C6.45194 77.1605 7.11952 77.1489 7.2303 77.6152L8.43953 82.7057L12.5097 83.3258C12.996 83.3999 13.0231 84.0907 12.544 84.2027L8.43953 85.1615L7.22827 89.8878C7.11126 90.3444 6.45863 90.3322 6.35874 89.8716L5.33735 85.1615L1.07046 84.2036C0.58691 84.0951 0.613249 83.3973 1.1036 83.3255L5.33735 82.7057L6.35749 77.6305Z" fill="#F6C96F"/>
        <defs xmlns="http://www.w3.org/2000/svg">
          <linearGradient id="paint0_linear_308_1366" x1="179.15" y1="89.7862" x2="170.015" y2="97.5812" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FFF4DE"/>
          <stop offset="1" stop-color="#F7C051"/>
        </linearGradient>
        <linearGradient xmlns="http://www.w3.org/2000/svg" id="paint18_linear_308_1366" x1="24.0938" y1="5.35213" x2="22.6542" y2="24.3281" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FFE2A8"/>
          <stop offset="1" stop-color="#F6C96F"/>
        </linearGradient>
        </defs>
      </svg>
    </div>
    <div :class="$style.heading">
      {{ $t('voucher.choose_amount') }}
    </div>
    <div :class="$style.list">
      <div
        v-for="(amount, index) in giftcardValues"
        :class="[
          $style.listItem,
          {
            [$style.listItemSingle]: giftcardValues.length === 1
          }
        ]"
        :key="index"
        @click="
          () => {
            giftcardAmount = amount;
            $emit('submit');
          }
        "
        v-test="'giftcard-amount'"
      >
        {{ currency(amount) }}
      </div>
    </div>
    <div v-if="settings.giftcard_custom_amount" :class="$style.customAmount">
      <div v-if="showCustomAmountInput" :class="$style.customInput">
        <BaseCurrencyInput
          v-model="customAmount"
          v-test="'giftcard-custom-input'"
        />
        <BaseButton
          @click="submitCustomAmount"
          v-test="'giftcard-custom-submit'"
        >
          {{ $t('general.submit') }}
        </BaseButton>
      </div>
      <div
        v-if="showCustomAmountInput"
        :class="[
          $style.customMinMax,
          {
            [$style.error]: showCustomAmountError
          }
        ]"
        v-test="'giftcard-custom-message'"
      >
        {{
          $t('voucher.custom_amount_minmax', {
            min: currency(settings.giftcard_custom_amount_min),
            max: currency(settings.giftcard_custom_amount_max)
          })
        }}
      </div>
      <div
        v-else
        :class="$style.customButton"
        @click="showCustomAmountInput = true"
        v-test="'giftcard-custom-button'"
      >
        {{ $t('voucher.custom_amount') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue';
import BaseCurrencyInput from '@/components/BaseCurrencyInput.vue';
import { currency } from '@/helpers';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

const { giftcardAmount } = storeToRefs(useBookingStore());
const { company, settings } = useCompanyStore();

const defaultVisualUrl =
  'https://res.cloudinary.com/salonized-eu-cld/image/upload/v1730893591/giftcard-picture/defaults/gc_default.jpg';

const isCustomVisual =
  company.settings.giftcard_picture &&
  !company.settings.giftcard_picture?.includes('defaults');

const emit = defineEmits(['submit']);

const giftcardValues = computed(() => {
  const { settings } = useCompanyStore();
  return settings.giftcard_values || [];
});

const customAmount = ref(settings.giftcard_custom_amount_min);
const showCustomAmountInput = ref(false);
const showCustomAmountError = ref(false);

const submitCustomAmount = () => {
  if (
    customAmount.value < settings.giftcard_custom_amount_min ||
    customAmount.value > settings.giftcard_custom_amount_max
  ) {
    showCustomAmountError.value = true;
  } else {
    showCustomAmountError.value = false;
    giftcardAmount.value = customAmount.value;
    emit('submit');
  }
};
</script>

<style lang="scss" module>
.base {
  padding: $spacing * 2;
}

.visualHolder {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing $spacing 2 * $spacing;
  margin-bottom: $spacing * 2;
  width: 100%;
}
.giftCardVisual {
  max-width: 180px;
  max-height: 180px;
}
.sparkles {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
}

.heading {
  margin-bottom: $spacing * 2;
  text-align: center;
  font-weight: bold;
}

.list {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: $spacing;
}

.listItem,
.customButton {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.listItem {
  padding: $spacing * 2;

  &:not(.listItemSingle) {
    width: calc(50% - #{$spacing * 0.5});
  }

  &.listItemSingle {
    width: 100%;
  }
}

.customButton {
  padding: $spacing * 1.5;
}

.customAmount {
  margin-top: $spacing;
}

.customInput {
  display: flex;
  gap: $spacing;
}

.customMinMax {
  font-size: 12px;
  text-align: center;
  margin-top: $spacing;

  &.error {
    color: red;
  }
}

.sparkle_one {
  animation: twinkle 8s ease-in-out infinite alternate;
}
.sparkle_two {
  animation: twinkle 5s ease-in-out infinite alternate;
}
.sparkle_three {
  animation: twinkle 6s ease-in-out infinite alternate;
}

@keyframes twinkle {
  0% {
    transform: scale(1.05);
    opacity: 1;
  }
  25% {
    transform: scale(0.95);
    opacity: 0;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  75% {
    transform: scale(0.95);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

</style>
